import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { createWavBlob } from "@utils/audioWavEncoder";
import * as kpiTurno from "@utils/kpiTurno";
import { uploadExamAudio } from "@services/examAudioUploader";
import { auth } from "@redux/firebase";
import { toast } from "react-toastify";
import {
  aggiungiInterim,
  trascrizioneIniziale,
  testoVisibile,
} from "./trascrizioneInterim";
import { esitoRisposta } from "./rispostaBreve";
import { aggiornaAscolto, ascoltoIniziale } from "./livelloMicrofono";
import { ricampiona } from "./ricampiona";
import { isShortAnswerConfirmEnabled } from "@config/featureFlags";

// Mappa a livello di modulo per bridge URL audio → saveQAExam
export const examAudioUrlMap = new Map();

// Define audio parameters
const SAMPLE_RATE = 48000;
const AUDIO_BITS = 16;
const AUDIO_CHANNELS = 1;
const BUFFER_SIZE = 4096;

// WebSocket URL
let WEBSOCKET_URL;

// Oltre questo tempo senza handshake la registrazione non parte: si chiude il
// socket e si torna al microfono, invece di lasciare la rotellina per sempre
// (CEO, preprod 02/09: pulsante bloccato sullo spinner dopo un click).
const APERTURA_SOCKET_MAX_MS = 8000;

// Quanto della domanda si manda al server: oltre la taglia lui
// (`DOMANDA_MAX_CARATTERI` in `trascrizione_openai.py`), ma una query string
// lunga la pagano tutti e due.
const DOMANDA_MAX_CARATTERI = 600;

// Il test end-to-end risponde con audio finto (--use-fake-device-for-media-stream):
// il server restituisce una trascrizione vuota, e senza questo ripiego il popup
// bloccherebbe l'avanzamento gia' alla prima domanda.
const TESTO_FALLBACK_CYPRESS =
  "Questa \u00e8 una risposta di test per l'esame. Sto fornendo una risposta abbastanza lunga per assicurarmi che ci sia contenuto sufficiente per essere elaborato correttamente dal sistema di valutazione.";

// --- INIZIO PATCH KEEP-ALIVE ---
const SILENCE_KEEPALIVE_INTERVAL = 5000; // ogni 5 secondi
const silenceKeepAliveRef = { current: null };
function sendFakeSilence(socket) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    const silenceBuffer = new Int16Array(BUFFER_SIZE); // tutti zeri
    socket.send(silenceBuffer.buffer);
  }
}
// --- FINE PATCH KEEP-ALIVE ---

const useAudioRecorder = (
  onFeedback,
  onHideAudioRecorder,
  onAddMessage,
  setUserAnswer,
  currentQuestion,
  qa,
  reviewMode,
  saveExamInInterrogationMode,
  saveInterrogationQA,
  setExamState,
  setShowEmptyResponsePopup,
  setAiIconState,
  nextQuestion,
  linguaRisposta
) => {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingAmount, setRecordingAmount] = useState(0);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [finalTranscript, setFinalTranscript] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState("it-IT"); // Lingua rilevata dalla trascrizione
  // Gli errori del server NON si travestono da trascrizione: hanno un posto loro.
  const [erroreRegistratore, setErroreRegistratore] = useState("");
  // Risposta sotto soglia in attesa che sia lo studente a decidere (STU-292).
  const [rispostaDaConfermare, setRispostaDaConfermare] = useState(null);
  // Sta parlando, ma troppo piano perche' il motore ci faccia qualcosa.
  const [microfonoDebole, setMicrofonoDebole] = useState(false);

  // Refs
  const audioContextRef = useRef(null);
  const audioSourceNodeRef = useRef(null);
  const scriptProcessorNodeRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const socketRef = useRef(null);
  const isRecordingRef = useRef(false);
  const rispostaDaConfermareRef = useRef(null);
  const ascoltoRef = useRef(ascoltoIniziale());
  // L'ultimo `INTERIM:` arrivato, che porta tutto il detto finora. Sta in un
  // ref perche' e' lo stato da cui nasce il testo, non quello che si disegna:
  // a schermo va `testoVisibile`.
  const trascrizioneRef = useRef(trascrizioneIniziale());
  // C'e' una risposta in volo di cui nessuno ha ancora dato conto? Serve a
  // distinguere un socket che si chiude a lavoro finito da uno che cade a
  // meta' risposta, che prima spegneva il pannello senza dire niente.
  const rispostaInVoloRef = useRef(false);
  const microfonoDeboleRef = useRef(false);
  // Il messaggio che chiude la registrazione e' gia' arrivato? Il server ne
  // manda DUE quando la trascrizione e' vuota (`FINAL:` e subito dopo il JSON
  // con `reason`), e senza questo la stessa risposta veniva consegnata due
  // volte: due popup, due `onHideAudioRecorder`, e in Cypress due
  // `nextQuestion`, cioe' una domanda saltata.
  const consegnaFattaRef = useRef(false);
  const audioChunksRef = useRef([]); // Accumula Int16Array chunks per creazione WAV
  // Lingua in cui lo studente risponde, quella del materiale (STU-529): letta
  // all'apertura del WebSocket, quindi in un ref come la domanda.
  const linguaRispostaRef = useRef(linguaRisposta);
  linguaRispostaRef.current = linguaRisposta;
  const latestPropsAndStateRef = useRef({
    /* ... */
  });

  const dispatch = useDispatch();

  // Effect to keep latestPropsAndStateRef updated
  useEffect(() => {
    latestPropsAndStateRef.current = {
      onFeedback,
      onHideAudioRecorder,
      onAddMessage,
      setUserAnswer,
      currentQuestion,
      qa,
      reviewMode,
      saveExamInInterrogationMode,
      saveInterrogationQA,
      setIsRecording,
      setIsTranscribing,
      setInterimTranscript,
      setFinalTranscript,
      setExamState,
      setShowEmptyResponsePopup,
      setAiIconState,
      nextQuestion
    };
  }, [
    onFeedback,
    onHideAudioRecorder,
    onAddMessage,
    setUserAnswer,
    currentQuestion,
    qa,
    reviewMode,
    saveExamInInterrogationMode,
    saveInterrogationQA,
    setIsRecording,
    setIsTranscribing,
    setInterimTranscript,
    setFinalTranscript,
    setExamState,
    setShowEmptyResponsePopup,
    setAiIconState,
    nextQuestion
  ]);

  // Il testo a schermo e la ricostruzione da cui nasce si azzerano INSIEME:
  // erano due cose separate e una restava indietro.
  const azzeraTrascrizione = useCallback(() => {
    trascrizioneRef.current = trascrizioneIniziale();
    setInterimTranscript("");
  }, []);

  // --- Fermare la cattura ---------------------------------------------------
  // La risposta puo' finire senza che lo studente prema stop: il timer del
  // silenzio del server consegna da solo dopo 30 s
  // (`SILENCE_TIMEOUT_SECONDS`), e il canale verso il motore puo' morire a
  // sessione avviata (`ERROR: Transcription session lost.`). Su quelle due
  // strade `stopRecording` non passa mai, e prima il registratore restava
  // acceso: continuava a spingere byte dentro una sessione finita e il WAV
  // della risposta non veniva caricato da nessuno.
  const caricaAudioRegistrato = useCallback(() => {
    const accumulatedChunks = audioChunksRef.current;
    audioChunksRef.current = [];
    if (accumulatedChunks.length === 0) return;

    const wavBlob = createWavBlob(
      accumulatedChunks,
      SAMPLE_RATE,
      AUDIO_CHANNELS,
      AUDIO_BITS
    );
    let userId = null;
    try {
      userId = JSON.parse(localStorage.getItem("userId"));
    } catch (e) {
      // Un `userId` illeggibile faceva alzare un'eccezione in mezzo allo stop:
      // si perde l'audio, non deve perdersi la risposta.
      console.warn("[useAudioRecorder] userId illeggibile:", e?.message);
    }
    const examId = localStorage.getItem("currentExamId");
    const questionIdx = latestPropsAndStateRef.current.currentQuestion;

    if (!userId || !examId) return;

    uploadExamAudio(wavBlob, userId, examId, questionIdx)
      .then((downloadURL) => {
        if (downloadURL) {
          examAudioUrlMap.set(`q${questionIdx}`, downloadURL);
          console.log(`[useAudioRecorder] Audio URL saved for q${questionIdx}`);
        }
      })
      .catch(() => {}); // Errore già gestito dentro uploadExamAudio
  }, []);

  /** Il microfono smette di spedire, e quello che ha gia' preso non si butta. */
  const fermaCattura = useCallback(() => {
    isRecordingRef.current = false;
    if (silenceKeepAliveRef.current) {
      clearInterval(silenceKeepAliveRef.current);
      silenceKeepAliveRef.current = null;
    }
    caricaAudioRegistrato();
  }, [caricaAudioRegistrato]);

  // --- Consegna della risposta ---------------------------------------------
  // Un solo posto in cui una risposta parlata entra nell'esame. Prima questo
  // blocco era copiato tre volte (FINAL, FINAL_TRANSCRIPT_RESULTS e la sua
  // variante JSON), e con lui il controllo sulla risposta vuota.
  const concludiRisposta = useCallback((testo, lingua) => {
    const {
      onFeedback,
      onHideAudioRecorder,
      setUserAnswer,
      currentQuestion,
      qa,
      reviewMode,
      saveExamInInterrogationMode,
      saveInterrogationQA,
      nextQuestion,
    } = latestPropsAndStateRef.current;

    if (setUserAnswer) setUserAnswer(testo);

    if (reviewMode) {
      if (onFeedback) {
        onFeedback(
          lingua ? { answer: testo, detectedLanguage: lingua } : { answer: testo }
        );
      }
    } else if (
      qa &&
      currentQuestion !== undefined &&
      saveInterrogationQA &&
      saveExamInInterrogationMode
    ) {
      const index = currentQuestion;
      const updatedQA = saveInterrogationQA({
        index,
        question: qa[index]?.question,
        answer: testo,
        correct_answer: qa[index]?.answer,
      });
      saveExamInInterrogationMode(currentQuestion, updatedQA);
    }

    if (onHideAudioRecorder) onHideAudioRecorder();
    // `nextQuestion` in ExamCard fa `answerText || userAnswer`: con la stringa
    // vuota (domanda saltata) ripescherebbe la risposta PRECEDENTE e la
    // manderebbe al correttore. Uno spazio e' vuoto per il correttore e non fa
    // scattare quel ripiego.
    if (!reviewMode && nextQuestion) nextQuestion(testo || " ");
  }, []);

  // Decide se la risposta entra nell'esame, apre il popup del vuoto, oppure
  // chiede allo studente (risposta breve, dietro flag).
  const consegnaRisposta = useCallback(
    (testoGrezzo, lingua) => {
      const {
        setExamState,
        setShowEmptyResponsePopup,
        setAiIconState,
        onHideAudioRecorder,
      } = latestPropsAndStateRef.current;

      // Da qui in poi la risposta ha una destinazione: se il socket si chiude
      // dopo, non c'e' piu' niente da recuperare e non si allarma nessuno.
      rispostaInVoloRef.current = false;

      let testo = testoGrezzo;
      const esito = esitoRisposta(testo);

      if (esito !== "ok") {
        if (window.Cypress) {
          console.log(
            "[CYPRESS MODE] Trascrizione vuota o troppo corta: uso il testo di ripiego per il test"
          );
          testo = TESTO_FALLBACK_CYPRESS;
          setFinalTranscript(testo);
        } else if (esito === "breve" && isShortAnswerConfirmEnabled()) {
          // Il registratore NON si nasconde: il popup chiede, e se lo studente
          // sceglie di riprovare il microfono e' ancora li'.
          if (setExamState) setExamState("listening");
          if (setAiIconState) setAiIconState("stopped");
          rispostaDaConfermareRef.current = { testo, lingua };
          setRispostaDaConfermare({ testo, lingua });
          return;
        } else if (esito === "vuota") {
          if (setExamState) setExamState("listening");
          if (onHideAudioRecorder) onHideAudioRecorder();
          if (setShowEmptyResponsePopup) setShowEmptyResponsePopup(true);
          if (setAiIconState) setAiIconState("stopped");
          return;
        }
        // "breve" col flag spento: prosegue esattamente come prima del flag.
      }

      concludiRisposta(testo, lingua);
    },
    [concludiRisposta]
  );

  // --- Le tre strade del popup della risposta breve (STU-292) ---------------

  /** "Si, e' completa": entra come oggi e viene votata. */
  const confermaRispostaBreve = useCallback(() => {
    const pendente = rispostaDaConfermareRef.current;
    rispostaDaConfermareRef.current = null;
    setRispostaDaConfermare(null);
    if (pendente) concludiRisposta(pendente.testo, pendente.lingua);
  }, [concludiRisposta]);

  /** "Salto la domanda": entra come vuota, prende lo 0 delle saltate. */
  const saltaDomandaBreve = useCallback(() => {
    rispostaDaConfermareRef.current = null;
    setRispostaDaConfermare(null);
    const { reviewMode, onHideAudioRecorder, setExamState } =
      latestPropsAndStateRef.current;
    if (reviewMode) {
      // Nella Ripetizione non c'e' una media da difendere: si chiude e basta.
      if (setExamState) setExamState("listening");
      if (onHideAudioRecorder) onHideAudioRecorder();
      return;
    }
    concludiRisposta("", null);
  }, [concludiRisposta]);

  /** "Riprovo a rispondere": la domanda resta, il microfono si riapre. */
  const riprovaRispostaBreve = useCallback(() => {
    rispostaDaConfermareRef.current = null;
    setRispostaDaConfermare(null);
    azzeraTrascrizione();
    setFinalTranscript("");
    setErroreRegistratore("");
    const { setExamState } = latestPropsAndStateRef.current;
    // "recording" e' il segnale che AudioRecorder aspetta per riaprire il
    // microfono. Niente e' entrato in interrogationQA.
    if (setExamState) setExamState("recording");
  }, [azzeraTrascrizione]);

  const handleWebSocketMessage = useCallback(
    (event) => {
      const rawMessage = event.data;
      const {
        setIsRecording,
        setIsTranscribing,
        setInterimTranscript,
        setFinalTranscript,
        setAiIconState,
        setExamState,
      } = latestPropsAndStateRef.current;

      if (typeof rawMessage !== "string") {
        console.warn("Received non-string WebSocket message:", rawMessage);
        return;
      }

      if (rawMessage.startsWith("INTERIM:")) {
        // Ogni `INTERIM:` porta TUTTO il detto finora (misurato dal vivo su
        // preprod: 177 messaggi su 43 s, zero ripartenze): `aggiungiInterim`
        // sostituisce quello che c'e', non gli accoda niente.
        const pezzo = rawMessage.substring("INTERIM:".length);
        setErroreRegistratore("");
        trascrizioneRef.current = aggiungiInterim(trascrizioneRef.current, pezzo);
        setInterimTranscript(testoVisibile(trascrizioneRef.current));
        return;
      }

      if (rawMessage.startsWith("FINAL:")) {
        // Quando non e' arrivata nessuna parola il server manda `FINAL:` E
        // SUBITO DOPO il JSON: senza questa guardia la stessa risposta entra
        // due volte (trascrizione_openai.py, ramo `if not testo`).
        if (consegnaFattaRef.current) return;
        consegnaFattaRef.current = true;
        const final = rawMessage.substring("FINAL:".length);
        fermaCattura();
        setFinalTranscript(final);
        azzeraTrascrizione();
        setIsTranscribing(false);
        setIsRecording(false);
        consegnaRisposta(final);
        return;
      }

      if (rawMessage.startsWith("ERROR:")) {
        // `.trim()`: il server manda "ERROR: <messaggio>", con lo spazio dopo
        // i due punti, e senza questo lo spazio finiva dentro il messaggio.
        const errorMessage = rawMessage.substring("ERROR:".length).trim();
        console.error("Backend Error:", errorMessage);
        setIsTranscribing(false);
        setIsRecording(false);
        // Prima qui finiva "Trascrizione completata." dentro la trascrizione:
        // un errore mostrato come successo. Adesso l'errore dice di essere un
        // errore, e quello che si era gia' sentito resta a schermo.
        setErroreRegistratore(errorMessage || "errore");
        rispostaInVoloRef.current = false;
        // Il microfono si ferma qui: senza questo continuava a spingere
        // audio dentro una sessione che nessuno ascolta piu'.
        fermaCattura();

        // E LA SESSIONE SI CHIUDE. Dopo `ERROR: Transcription session lost.`
        // il server lascia il socket aperto (aspetta ancora END_RECORDING),
        // ma il canale verso il motore e' morto: `startRecording` trovava un
        // socket OPEN, lo riusava, e il secondo tentativo dello studente
        // finiva in una sessione sorda — pannello su "Ti sto ascoltando",
        // nessun INTERIM, e allo stop il correttore riceveva il pezzo del
        // primo tentativo o niente. Chiudendolo, il tentativo dopo apre una
        // sessione nuova.
        if (socketRef.current) {
          try {
            socketRef.current.close();
          } catch (e) {
            console.warn("[useAudioRecorder] chiusura socket dopo ERROR:", e?.message);
          }
          socketRef.current = null;
        }

        // Il registratore NON si smonta: qui c'era `if (!finalTranscript)`, che
        // leggeva dalla chiusura di questo callback (stabile) il valore del
        // primo render, cioe' "" per sempre. Era quindi sempre vero, il
        // registratore spariva sempre, e con lui il messaggio d'errore appena
        // scritto: l'utente non lo vedeva mai. Restando montato il messaggio si
        // legge, e il microfono e' li' per riprovare — come gia' fa il ramo
        // dell'errore di socket.
        if (setExamState) setExamState("listening");
        if (setAiIconState) setAiIconState("stopped");
        return;
      }

      // FINAL_TRANSCRIPT_RESULTS, nelle due forme in cui arriva: col prefisso,
      // oppure come JSON puro con `type`.
      let jsonData = null;
      if (rawMessage.startsWith("FINAL_TRANSCRIPT_RESULTS:")) {
        try {
          jsonData = JSON.parse(
            rawMessage.substring("FINAL_TRANSCRIPT_RESULTS:".length)
          );
        } catch (error) {
          console.error("Error parsing FINAL_TRANSCRIPT_RESULTS:", error);
          return;
        }
      } else {
        try {
          const parsed = JSON.parse(rawMessage);
          if (!parsed || parsed.type !== "FINAL_TRANSCRIPT_RESULTS") {
            console.warn("Received unknown JSON message structure:", parsed);
            return;
          }
          jsonData = parsed;
        } catch (error) {
          console.warn(
            "Received unknown string message that is not JSON either:",
            rawMessage.substring(0, 100) + "..."
          );
          return;
        }
      }

      console.log(
        "FRONTEND_WEBSOCKET: Received FINAL_TRANSCRIPT_RESULTS:",
        jsonData
      );
      // Stessa guardia del `FINAL:`: una risposta si consegna una volta sola,
      // anche quando il server manda tutti e due i messaggi.
      if (consegnaFattaRef.current) return;
      consegnaFattaRef.current = true;
      // La registrazione puo' chiudersi da sola (timer del silenzio, sessione
      // persa): li' lo stop non e' mai stato premuto e il turno comincia qui,
      // con `finale_ms=0` a dirlo.
      if (!kpiTurno.aperto()) kpiTurno.avvia();
      kpiTurno.segna('finale');
      // Il finale puo' arrivare senza che lo studente abbia premuto stop
      // (`reason: "silence_timeout"`): la cattura si ferma qui, e il WAV della
      // risposta parte come se lo stop l'avesse premuto lui.
      fermaCattura();
      const finalSessionTranscript = jsonData.transcript;
      const langDetected = jsonData.detected_language || "it-IT";

      setFinalTranscript(finalSessionTranscript);
      azzeraTrascrizione();
      setIsTranscribing(false);
      setIsRecording(false);
      setDetectedLanguage(langDetected);
      console.log(
        "FRONTEND_WEBSOCKET: Detected language:",
        langDetected,
        "reason:",
        jsonData.reason
      );

      consegnaRisposta(finalSessionTranscript, langDetected);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [consegnaRisposta, azzeraTrascrizione, fermaCattura]
  );

  // Define audio cleanup logic as a separate function
  const cleanupAudioResources = useCallback(() => {
    if (scriptProcessorNodeRef.current) {
      scriptProcessorNodeRef.current.onaudioprocess = null;
      try {
        scriptProcessorNodeRef.current.disconnect();
      } catch (e) {
        console.warn(
          "Error disconnecting ScriptProcessorNode during cleanup:",
          e
        );
      }
      scriptProcessorNodeRef.current = null;
    }
    if (audioSourceNodeRef.current) {
      try {
        audioSourceNodeRef.current.disconnect();
      } catch (e) {
        console.warn("Error disconnecting AudioSourceNode during cleanup:", e);
      }
      audioSourceNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch((e) => {
        console.error("Error closing AudioContext during cleanup:", e);
      });
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    audioChunksRef.current = [];
  }, []);

  // WebSocket close handler
  const handleWebSocketClose = useCallback(
    (event) => {
      const { setIsRecording, setIsTranscribing, setAiIconState } =
        latestPropsAndStateRef.current;
      setIsRecording(false);
      setIsTranscribing(false);

      // Un socket puo' chiudersi pulito anche a meta' risposta: riavvio del
      // server, istanza riciclata, rete che salta. Non arriva nessun evento
      // d'errore, quindi prima il pannello spariva e basta e lo studente
      // restava davanti al microfono senza sapere che la risposta era persa.
      if (rispostaInVoloRef.current) {
        rispostaInVoloRef.current = false;
        setErroreRegistratore("connessione chiusa dal server");
        if (setAiIconState) setAiIconState("stopped");
      }

      cleanupAudioResources(); // Clean up audio resources on WebSocket close
      // socketRef.current is nulled in the useEffect cleanup or startRecording error handling
    },
    [cleanupAudioResources]
  );

  // WebSocket error handler
  const handleWebSocketError = useCallback(
    (error) => {
      console.error("WebSocket error:", error);
      const { setIsRecording, setIsTranscribing } =
        latestPropsAndStateRef.current;
      setIsRecording(false);
      setIsTranscribing(false);
      // Prima "WebSocket Error." finiva nella trascrizione, in inglese e con
      // il nome del protocollo dentro. Ora e' un errore dichiarato tale.
      setErroreRegistratore("connessione interrotta");
      // Closing the socket will trigger handleWebSocketClose, which cleans up audio
      if (
        socketRef.current &&
        socketRef.current.readyState !== WebSocket.CLOSED
      ) {
        socketRef.current.close();
      }
    },
    [latestPropsAndStateRef]
  );

  // --- Start/Stop Recording Functions ---
  const stopRecording = useCallback(() => {
    // Da qui parte il cronometro del turno: e' il momento in cui lo studente
    // smette di parlare e comincia ad aspettare. Tutto il resto della riga
    // KPI TURNO e' misurato da questo istante.
    kpiTurno.avvia();
    // --- PATCH: stop keep-alive timer ---
    if (silenceKeepAliveRef.current) {
      clearInterval(silenceKeepAliveRef.current);
      silenceKeepAliveRef.current = null;
    }
    isRecordingRef.current = false; // Synchronous ref update
    setIsRecording(false); // UI state update
    setIsTranscribing(true); // Indicate backend processing is starting
    setRecordingAmount(0); // Reset volume for wave visualization

    if (
      scriptProcessorNodeRef.current &&
      audioContextRef.current?.destination
    ) {
      try {
        scriptProcessorNodeRef.current.disconnect(
          audioContextRef.current.destination
        );
      } catch (e) {
        console.warn(
          "Error disconnecting ScriptProcessorNode from destination:",
          e
        );
      }
    }

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send("END_RECORDING");
    } else {
      console.warn(
        "WebSocket not open when trying to send END_RECORDING signal on stop."
      );
      latestPropsAndStateRef.current.setIsTranscribing(false);
    }

    // Crea WAV blob dai chunk accumulati e lancia upload su Firebase Storage
    // (fire-and-forget). Stessa strada che prendono le chiusure che NON
    // passano da qui: il finale del timer di silenzio e l'errore di sessione.
    caricaAudioRegistrato();

    // Il testo sentito finora RESTA a schermo mentre il server trascrive: era
    // qui che spariva tutto, e lo studente guardava una schermata vuota per
    // 9 secondi mediani (p90 35). Si ripulisce quando riparte la
    // registrazione, non quando finisce.
  }, [latestPropsAndStateRef, caricaAudioRegistrato]);

  // This function handles WebSocket connection and audio setup
  const startRecording = useCallback(async () => {
    // Prevent starting if already loading, recording, or transcribing
    if (isLoading || isRecording || isTranscribing) {
      return;
    }

    setIsLoading(true); // Start loading indicator
    audioChunksRef.current = []; // Buffer pulito per nuova registrazione

    try {
      if (
        !socketRef.current ||
        socketRef.current.readyState === WebSocket.CLOSED
      ) {
        switch (process.env.REACT_APP_ENVIRONMENT) {
          case "development":
            WEBSOCKET_URL = "ws://" + process.env.REACT_APP_API_URL_DEVELOPMENT;
            break;
          case "preproduction":
            WEBSOCKET_URL =
              "wss://" + process.env.REACT_APP_API_URL_PREPRODUCTION;
            break;
          default:
            WEBSOCKET_URL = "wss://" + process.env.REACT_APP_API_URL_PRODUCTION;
        }
        // Il token viaggia in query string: sull'handshake websocket il browser
        // non permette header custom. Se manca, il server (fase permissiva) lo
        // logga e lascia passare — non si rompe una registrazione in corso.
        let wsToken = "";
        try {
          wsToken = (await auth?.currentUser?.getIdToken?.()) || "";
        } catch (e) {
          console.warn("[audioRecorder] token non disponibile:", e?.message);
        }
        const parametri = new URLSearchParams();
        if (wsToken) parametri.set("token", wsToken);
        // I byte che partono da qui sono SEMPRE a 48 kHz: quando il contesto
        // audio gira a un'altra frequenza li riporta li' `ricampiona`. Il
        // server lo usa per la durata dell'audio nella riga KPI (`rate_hz`),
        // che senza il parametro deve tirare a indovinare.
        parametri.set("sampleRate", String(SAMPLE_RATE));
        // Con la domanda, il motore sa cosa aspettarsi di sentire: "Schuld",
        // "Haftung", "auctoritas" sono termini che senza contesto tornano
        // storpiati, e la trascrizione e' la materia prima del voto. Viaggia
        // in query string come il token ed e' facoltativa: i client che non
        // la mandano (le app iOS gia' in giro) aprono la stessa sessione di
        // prima. (Due rami l'hanno aggiunta in parallelo, `36874149` e
        // `66f17943`: questa e' la fusione dei due.)
        let domanda = "";
        try {
          const { qa: elenco, currentQuestion: indice } =
            latestPropsAndStateRef.current || {};
          domanda = String(elenco?.[indice]?.question || "").slice(0, DOMANDA_MAX_CARATTERI);
        } catch (e) {
          console.warn("[audioRecorder] domanda non disponibile:", e?.message);
        }
        if (domanda) parametri.set("domanda", domanda);
        // Facoltativa come la domanda: il server la usa per il contesto della
        // trascrizione, che senza resta in italiano (STU-529).
        const lingua = String(linguaRispostaRef.current || "").slice(0, 2).toLowerCase();
        if (lingua) parametri.set("lingua", lingua);
        socketRef.current = new WebSocket(
          `${WEBSOCKET_URL}/websocket?${parametri.toString()}`
        );

        // Await the WebSocket connection to open using a Promise
        await new Promise((resolve, reject) => {
          if (!socketRef.current) {
            // Safety check
            return reject(new Error("WebSocket failed to create."));
          }
          const socketInApertura = socketRef.current;
          const scadenza = setTimeout(() => {
            if (socketInApertura.readyState === WebSocket.OPEN) return;
            try { socketInApertura.close(); } catch (_) {}
            if (socketRef.current === socketInApertura) socketRef.current = null;
            reject(new Error("Il server non risponde, riprova tra un attimo."));
          }, APERTURA_SOCKET_MAX_MS);
          socketRef.current.onopen = () => {
            clearTimeout(scadenza);
            resolve();
          };
          socketRef.current.onerror = (event) => {
            clearTimeout(scadenza);
            console.error("WebSocket connection error:", event); // Kept error
            // Clean up on error. handleWebSocketError will also be called.
            // Nullify ref immediately on error for cleanup
            socketRef.current = null;
            reject(new Error("WebSocket connection failed."));
          };
          // Assign other handlers *after* onopen/onerror are set up for the promise
          if (socketRef.current) {
            // Safety check
            socketRef.current.onmessage = handleWebSocketMessage;
            // handleWebSocketClose and handleWebSocketError are assigned here too,
            // but the promise relies on the specific onopen/onerror assigned above.
            socketRef.current.onclose = handleWebSocketClose;
            // socketRef.current.onerror is already assigned for the promise rejection
          }
        });
      } else if (socketRef.current.readyState === WebSocket.CONNECTING) {
        // Await the WebSocket connection to open if it's still connecting
        await new Promise((resolve, reject) => {
          if (!socketRef.current) {
            // Safety check
            return reject(
              new Error("WebSocket was connecting but ref is null.")
            );
          }
          // If it opens while we're waiting
          const existingOnOpen = socketRef.current.onopen;
          socketRef.current.onopen = () => {
            if (existingOnOpen) existingOnOpen.call(socketRef.current); // Call original handler
            resolve();
          };
          // If it errors while we're waiting
          const existingOnError = socketRef.current.onerror;
          socketRef.current.onerror = (event) => {
            console.error("WebSocket connection error while waiting:", event); // Kept error
            if (existingOnError) existingOnError.call(socketRef.current, event); // Call original handler
            socketRef.current = null; // Nullify ref immediately on error
            reject(new Error("WebSocket connection failed while waiting."));
          };
        });
      } else {
        // Re-assign handlers just in case (safe with useCallback)
        if (socketRef.current) {
          // Safety check
          socketRef.current.onmessage = handleWebSocketMessage;
          socketRef.current.onclose = handleWebSocketClose;
          socketRef.current.onerror = handleWebSocketError;
        }
      }

      // Ensure WebSocket is actually open before proceeding
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        // This check should ideally pass if the await succeeded, but as a final safety
        console.error("Final WebSocket state check failed."); // Added Log
        throw new Error("WebSocket is not open after connection attempt.");
      }

      // --- Step 2: Ensure Audio Setup is Complete ---
      if (
        !audioContextRef.current ||
        !audioSourceNodeRef.current ||
        !scriptProcessorNodeRef.current ||
        !mediaStreamRef.current
      ) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: { sampleRate: SAMPLE_RATE, channelCount: AUDIO_CHANNELS },
          });
          mediaStreamRef.current = stream;

          // La frequenza si CHIEDE al contesto, non solo a `getUserMedia`:
          // il vincolo passato a `getUserMedia` non decide la frequenza
          // dell'AudioContext, che il browser prende dal dispositivo di
          // sistema (44,1 kHz su molti Mac e iPhone). Safari vecchio non
          // accetta l'opzione e alza un'eccezione: si ripiega sul contesto
          // di default, e ci pensa `ricampiona` piu' sotto.
          const Contesto = window.AudioContext || window.webkitAudioContext;
          let audioContext;
          try {
            audioContext = new Contesto({ sampleRate: SAMPLE_RATE });
          } catch (e) {
            console.warn(
              "[audioRecorder] contesto a 48 kHz non accettato:",
              e?.message
            );
            audioContext = new Contesto();
          }
          audioContextRef.current = audioContext;

          const sourceNode = audioContext.createMediaStreamSource(stream);
          audioSourceNodeRef.current = sourceNode;

          const scriptProcessorNode = audioContext.createScriptProcessor(
            BUFFER_SIZE,
            AUDIO_CHANNELS,
            AUDIO_CHANNELS
          );
          scriptProcessorNodeRef.current = scriptProcessorNode;

          sourceNode.connect(scriptProcessorNode);
          scriptProcessorNode.connect(audioContext.destination);

          scriptProcessorNode.onaudioprocess = (event) => {
            const currentIsRecording = isRecordingRef.current;
            if (!currentIsRecording) return;
            const audioData = event.inputBuffer.getChannelData(0);
            
            // Calculate audio volume for wave visualization
            let sum = 0;
            for (let i = 0; i < audioData.length; i++) {
              sum += Math.abs(audioData[i]);
            }
            const averageVolume = sum / audioData.length;
            const normalizedVolume = Math.min(averageVolume * 10, 1); // Amplify and normalize to 0-1
            setRecordingAmount(normalizedVolume);

            // Le onde dicono "il microfono ti prende". Questo conto dice
            // "il motore ti capisce", che e' un'altra cosa: sulle
            // registrazioni vere del 06/09 la risposta da cui il motore non
            // ha cavato una parola stava sotto 0,25 per il 99,2% del tempo,
            // mentre quelle trascritte ci stavano sopra un terzo del tempo.
            // Il conto vive in un ref: un setState ogni 85 ms sarebbe un
            // re-render ogni 85 ms per un valore che cambia una volta sola.
            const durataBloccoMs =
              (audioData.length / audioContextRef.current.sampleRate) * 1000;
            const ascolto = aggiornaAscolto(
              ascoltoRef.current,
              normalizedVolume,
              durataBloccoMs
            );
            ascoltoRef.current = ascolto;
            if (ascolto.avviso !== microfonoDeboleRef.current) {
              microfonoDeboleRef.current = ascolto.avviso;
              setMicrofonoDebole(ascolto.avviso);
            }
            
            // Il server dimezza i campioni assumendo 48 kHz (`_dimezza`) e
            // l'intestazione del WAV scrive 48000: se il contesto gira a
            // 44,1 kHz la voce arriva l'8,8% piu' veloce e piu' acuta, per
            // tutta la risposta. A 48 kHz questa riga non fa niente.
            const perIlServer = ricampiona(
              audioData,
              audioContextRef.current.sampleRate
            );

            const int16Array = new Int16Array(perIlServer.length);
            for (let i = 0; i < perIlServer.length; i++) {
              int16Array[i] = Math.max(
                -32768,
                Math.min(32767, perIlServer[i] * 32767.5)
              );
            }

            // Accumula una copia del chunk per la creazione del file WAV
            audioChunksRef.current.push(new Int16Array(int16Array));

            if (
              int16Array.buffer.byteLength > 0 &&
              socketRef.current &&
              socketRef.current.readyState === WebSocket.OPEN
            ) {
              console.debug(
                `Sending audio chunk (${int16Array.buffer.byteLength} bytes) at ${audioContextRef.current.sampleRate} Hz.`
              );
              socketRef.current.send(int16Array.buffer);
            } else if (int16Array.buffer.byteLength > 0) {
              console.debug(
                `Audio data available but not sending. Conditions: isRecording=${currentIsRecording}, socket open=${
                  socketRef.current?.readyState === WebSocket.OPEN
                }`
              );
            }
          };
        } catch (error) {
          console.error(
            "Error accessing microphone or setting up audio:",
            error
          );
          throw new Error("microfono non accessibile, controlla i permessi del browser."); // Throw to be caught by outer catch
        }
      } else {
        // Reconnect processor to destination if it was disconnected
        if (
          scriptProcessorNodeRef.current &&
          audioContextRef.current?.destination
        ) {
          try {
            scriptProcessorNodeRef.current.connect(
              audioContextRef.current.destination
            );
          } catch (e) {
            console.warn("Error connecting processor to destination:", e);
          }
        }
      }

      // Ensure audio context is running if it was suspended (important if context is reused)
      if (
        audioContextRef.current &&
        audioContextRef.current.state === "suspended"
      ) {
        await audioContextRef.current.resume().catch((e) => {
          console.error("Error resuming AudioContext:", e);
        });
      }

      // Su iOS il contesto audio nasce sospeso e si sblocca solo DENTRO il
      // gesto dell'utente: qui il gesto e' gia' scaduto (in mezzo ci sono
      // l'attesa del token e l'handshake del socket), e quando la
      // registrazione parte da `examState` non c'e' proprio un gesto. Un
      // contesto sospeso non chiama mai `onaudioprocess`: niente onde, niente
      // testo, niente byte al server. Lo studente parlava due minuti davanti a
      // "Ti sto ascoltando" e si ritrovava il popup della risposta vuota.
      if (
        audioContextRef.current &&
        audioContextRef.current.state === "suspended"
      ) {
        throw new Error("il microfono non si e' aperto, tocca di nuovo il pulsante.");
      }

      // --- Step 3: If BOTH WebSocket and Audio are Ready, THEN Start Recording State ---
      isRecordingRef.current = true; // Synchronous ref update
      setIsRecording(true); // UI state update
      setIsTranscribing(false); // Reset transcribing state
      azzeraTrascrizione(); // Testo a schermo e ricostruzione, insieme
      setFinalTranscript(""); // Clear previous final transcript
      setErroreRegistratore(""); // L'errore del giro prima non sopravvive
      // Da qui c'e' una risposta da perdere: se il socket si chiude prima che
      // qualcuno ne dia conto, il pannello lo dice invece di sparire.
      rispostaInVoloRef.current = true;
      consegnaFattaRef.current = false;
      ascoltoRef.current = ascoltoIniziale();
      microfonoDeboleRef.current = false;
      setMicrofonoDebole(false);

      // --- PATCH: avvia il timer keep-alive ---
      if (!silenceKeepAliveRef.current) {
        silenceKeepAliveRef.current = setInterval(() => {
          sendFakeSilence(socketRef.current);
        }, SILENCE_KEEPALIVE_INTERVAL);
      }

    } catch (error) {
      console.error("Failed to start recording process:", error); // Kept error
      // Niente alert (blocca la pagina): un toast, e il pulsante torna al
      // microfono cosi' lo studente riprova con un click.
      toast.error(`Registrazione non partita: ${error.message}`, { autoClose: 4000 });
      setIsRecording(false);
      setIsTranscribing(false);
      isRecordingRef.current = false;
      const { onHideAudioRecorder, setExamState } = latestPropsAndStateRef.current;
      if (setExamState) setExamState("listening");
      if (onHideAudioRecorder) onHideAudioRecorder();
      // La sessione aperta a meta' non resta in giro: senza questo il
      // tentativo successivo trovava un socket OPEN e lo riusava, e il server
      // teneva viva una trascrizione che non riceveva piu' audio.
      if (socketRef.current) {
        try {
          socketRef.current.close();
        } catch (e) {
          console.warn("[audioRecorder] chiusura socket dopo avvio fallito:", e?.message);
        }
        socketRef.current = null;
      }
      // Cleanup is handled by handleWebSocketError/Close or useEffect on unmount
    } finally {
      setIsLoading(false); // Always stop loading when the process finishes (success or failure)
    }
  }, [
    handleWebSocketMessage,
    handleWebSocketClose,
    handleWebSocketError,
    isLoading,
    isRecording,
    isTranscribing,
    latestPropsAndStateRef,
    azzeraTrascrizione,
  ]);

  // Effect for cleaning up audio and WebSocket resources on unmount
  useEffect(() => {
    return () => {
      // --- PATCH: cleanup keep-alive timer ---
      if (silenceKeepAliveRef.current) {
        clearInterval(silenceKeepAliveRef.current);
        silenceKeepAliveRef.current = null;
      }
      cleanupAudioResources(); // Use the shared audio cleanup function
      // Also close the WebSocket on unmount if it exists
      if (
        socketRef.current &&
        socketRef.current.readyState !== WebSocket.CLOSED
      ) {
        socketRef.current.close(); // This will trigger handleWebSocketClose
      }
      socketRef.current = null; // Ensure socket ref is nullified on unmount
    };
  }, [cleanupAudioResources]); // Dependency on the shared audio cleanup function

  // Return values
  return {
    recordingAmount,
    isTranscribing,
    isRecording,
    interimTranscript,
    finalTranscript,
    startRecording,
    stopRecording,
    isLoading, // Return isLoading state for button feedback
    detectedLanguage, // Lingua rilevata dalla trascrizione (it-IT, en-US, es-ES)
    erroreRegistratore, // Messaggio d'errore, separato dalla trascrizione
    microfonoDebole, // Sta parlando, ma sotto il livello che il motore legge
    rispostaDaConfermare, // { testo, lingua } quando serve una scelta dello studente
    confermaRispostaBreve,
    saltaDomandaBreve,
    riprovaRispostaBreve,
  };
};

export default useAudioRecorder;
